import {useDispatch, useSelector} from "react-redux";
import {useHistory, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {loadLettersByStack, loadStack, downloadLetter, downloadAttachment} from "../../store/app/app.slice";
import {Col, Pagination, Row, Spin, Card, Button, Space} from "antd";
import StackLetter from "./StackLetter";
import {ProcessedLetter} from "ps-letter-mgmtrest-client";
import {LeftCircleOutlined, DownloadOutlined, FilePdfOutlined, FileWordOutlined} from "@ant-design/icons";
import {getProcessedLetterStatusColor, getProcessedLetterStatusLabel} from "../helper/labelHelper";
import {getAuthenticatedUser} from "../../utils/authHelper";
import {getSortedLettersFlat} from "../../utils/stackSortingUtils";

export default function StackLetterDetail(props) {
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();
    const [currentLetterSelected, setCurrentLetterSelected] = useState(null);
    const {currentStack} = useSelector((state) => state.app);
    const {lettersByStack} = useSelector((state) => state.app);
    const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
    
    const sortedLetters = (currentStack?.assignedStackType === "ANSWER"
        || currentStack?.assignedStackType === "REVIEW_ANSWER")
        ? getSortedLettersFlat(currentStack, lettersByStack)
        : lettersByStack;
    
    // Helper function to find next non-forwarded letter
    const findNextNonForwardedLetter = (currentIndex) => {
        if (!sortedLetters || currentIndex >= sortedLetters.length - 1) return null;
        
        for (let i = currentIndex + 1; i < sortedLetters.length; i++) {
            if (sortedLetters[i].internalStatus !== 'LETTER_INTERNAL_FORWARDED') {
                return { index: i, letter: sortedLetters[i] };
            }
        }
        return null;
    };
    
    // Helper function to find previous non-forwarded letter
    const findPreviousNonForwardedLetter = (currentIndex) => {
        if (!sortedLetters || currentIndex <= 0) return null;
        
        for (let i = currentIndex - 1; i >= 0; i--) {
            if (sortedLetters[i].internalStatus !== 'LETTER_INTERNAL_FORWARDED') {
                return { index: i, letter: sortedLetters[i] };
            }
        }
        return null;
    };
    
    useEffect(() => {
        dispatch(loadStack({finalData: {id: params?.stackId}}))
        dispatch(loadLettersByStack({finalData: {stackId: params?.stackId}}))
        
    }, []);
    
    useEffect(() => {
        if(sortedLetters && sortedLetters.length > 0) {
            setCurrentLetterIndex(sortedLetters.findIndex(letter => letter.id === params?.letterId))
            setCurrentLetterSelected(sortedLetters.find(letter => letter.id === params?.letterId))
            
        }
    }, [sortedLetters, params?.letterId])

    const handleDownload = () => {
        dispatch(downloadLetter({
            finalData: {id: params?.letterId},
            callbackSuccess: (response) => {
                const contentDisposition = response.headers["content-disposition"];
                const filename = contentDisposition ? contentDisposition.split("=")[1] : `letter-${params?.letterId}.docx`;
                const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        }));
    };

    const handleDownloadPdf = () => {
        if (!currentLetterSelected?.attachmentIdAnswerPdf) {
            console.error("No answer PDF available to download");
            return;
        }
        dispatch(downloadAttachment({
            finalData: {id: currentLetterSelected.attachmentIdAnswerPdf},
            callbackSuccess: () => {},
            callbackError: (err) => {
                console.error("PDF download failed", err);
            }
        }));
    };

    const handleDownloadAnswerWord = () => {
        if (!currentLetterSelected?.attachmentIdAnswer) {
            console.error("No answer Word document available to download");
            return;
        }
        dispatch(downloadAttachment({
            finalData: {id: currentLetterSelected.attachmentIdAnswer},
            callbackSuccess: () => {},
            callbackError: (err) => {
                console.error("Answer Word download failed", err);
            }
        }));
    };

    const navigateToLetterView = (record) => {
        history.push("/process/"+params?.stackType+"/stacks/"+params?.stackId+"/"+record.id)
    }

    const onLetterProcessed = () => {
        if(sortedLetters && sortedLetters.length > 0)
        {
            const nextLetter = findNextNonForwardedLetter(currentLetterIndex);
            if (nextLetter) {
                history.push("/process/"+params?.stackType+"/stacks/"+params?.stackId+"/"+nextLetter.letter.id)
            } else {
                history.push("/process/"+params?.stackType+"/stacks/"+params?.stackId)
            }
        }
    }
    
    const handlePaginationChange = (page) => {
        const targetIndex = page - 1;
        const targetLetter = sortedLetters[targetIndex];
        
        // Check if this is a manual page selection (large jump in page numbers)
        const pageDifference = Math.abs(page - (currentLetterIndex + 1));
        const isManualSelection = pageDifference > 1;
        
        // If target letter is forwarded and NOT a manual selection, find the appropriate non-forwarded letter
        if (targetLetter && targetLetter.internalStatus === 'LETTER_INTERNAL_FORWARDED' && !isManualSelection) {
            // Determine direction: if going backwards (page < currentLetterIndex + 1), find previous non-forwarded
            if (page < currentLetterIndex + 1) {
                const prevNonForwarded = findPreviousNonForwardedLetter(targetIndex);
                if (prevNonForwarded) {
                    setCurrentLetterIndex(prevNonForwarded.index);
                    navigateToLetterView(prevNonForwarded.letter);
                    return;
                }
            } else {
                // Going forwards, find next non-forwarded
                const nextNonForwarded = findNextNonForwardedLetter(targetIndex);
                if (nextNonForwarded) {
                    setCurrentLetterIndex(nextNonForwarded.index);
                    navigateToLetterView(nextNonForwarded.letter);
                    return;
                }
            }
        }
        
        // For manual page selection or when no non-forwarded letter found, navigate to exact target
        setCurrentLetterIndex(targetIndex);
        navigateToLetterView(targetLetter);
    };
    
    // Use total letters count (including internal forwarded)
    const effectiveTotal = sortedLetters ? sortedLetters.length : 0;
    
    return <>
        { (currentStack && currentLetterSelected && ""+currentStack.id === params.stackId && ""+currentLetterSelected.id === params.letterId) ? 
            <>
                <Row>
                    <Col span={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title={<Space>
                                <span>{currentStack.assignedStackType.charAt(0).toUpperCase() + currentStack.assignedStackType.slice(1).toLowerCase() + "-Stack #"+currentStack.id}</span>

                                <Button type="text" style={{backgroundColor: getProcessedLetterStatusColor(currentLetterSelected), color: 'white'}}>
                                    {getProcessedLetterStatusLabel(currentLetterSelected)}
                                </Button>
                                    <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                                        Download
                                    </Button>
                                    {currentStack.assignedStackType === "REVIEW_ANSWER" && (
                                        <>
                                            {currentLetterSelected.attachmentIdAnswerPdf && (
                                                <Button type="default" icon={<FilePdfOutlined />} onClick={handleDownloadPdf} style={{color: '#722ed1', borderColor: '#722ed1'}}>
                                                    Download Answer PDF
                                                </Button>
                                            )}
                                            {currentLetterSelected.attachmentIdAnswer && (
                                                <Button type="default" icon={<FileWordOutlined />} onClick={handleDownloadAnswerWord} style={{color: '#1890ff', borderColor: '#1890ff'}}>
                                                    Download Answer Word
                                                </Button>
                                            )}
                                        </>
                                    )}
                            </Space>}
                            extra={<Space>
                                    <Button className={"ant-tag-close-icon"} type="default" onClick={() => {window.history.back()}}>
                                        <LeftCircleOutlined/>Back
                                    </Button>
                                </Space>}
                        >
                        </Card>
                    </Col>
                </Row>
                <Row style={{}}>
                    <Col span={24} justify="center" style={{textAlign: "center"}}>
                        <div>
                            <Pagination simple current={currentLetterIndex+1} total={effectiveTotal} pageSize={1} onChange={handlePaginationChange}/></div>
                    </Col>
                </Row>
                <Row style={{height: "85%"}}>
                    <Col span={24}>
                        <StackLetter key={params.letterId} letter={currentLetterSelected} stack={currentStack} onLetterProcessed={onLetterProcessed}/>
                    </Col>
                </Row>
            </>
            : <Spin tip="Loading..."></Spin>}
    </>
}
